import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";
import { useNavigate } from "react-router-dom";
import { getToken } from "../../utils/auth";

const API_URL = CONFIG.API_URL;

// Each tab has explicit endpoint config to avoid URL mismatch bugs
const TABS = [
    {
        id: "products",
        label: "Products",
        fetchUrl: "/api/products?trash=true",
        restoreUrl: (id) => `/api/products/${id}/restore`,
        forceUrl: (id) => `/api/products/${id}/force`,
        restoreMethod: "put",
        forceMethod: "delete",
    },
    {
        id: "categories",
        label: "Categories",
        fetchUrl: "/api/categories?trash=true",
        restoreUrl: (id) => `/api/categories/${id}/restore`,
        forceUrl: (id) => `/api/categories/${id}/force`,
        restoreMethod: "put",
        forceMethod: "delete",
    },
    {
        id: "hero",
        label: "Hero Slides",
        fetchUrl: "/api/hero/admin?trash=true",
        restoreUrl: (id) => `/api/hero/${id}/restore`,
        forceUrl: (id) => `/api/hero/${id}/force`,
        restoreMethod: "put",
        forceMethod: "delete",
    },
    {
        id: "about",
        label: "About Us",
        fetchUrl: "/api/about-us?trash=true",
        restoreUrl: (id) => `/api/about-us/${id}/restore`,
        forceUrl: (id) => `/api/about-us/${id}/force`,
        restoreMethod: "put",
        forceMethod: "delete",
    },
    {
        id: "contact_info",
        label: "Contact Info",
        fetchUrl: "/api/admin/contact-info?trash=true",
        restoreUrl: (id) => `/api/admin/contact-info/${id}/restore`,
        forceUrl: (id) => `/api/admin/contact-info/${id}/force`,
        restoreMethod: "put",
        forceMethod: "delete",
    },
    {
        id: "contact_messages",
        label: "Messages",
        fetchUrl: "/api/contact/admin?trash=true",
        bulkRestoreUrl: "/api/contact/admin/bulk-restore",
        bulkForceUrl: "/api/contact/admin/bulk-force-delete",
        isBulk: true,
    },
    {
        id: "reviews",
        label: "Reviews",
        fetchUrl: "/api/admin/reviews?trash=true",
        bulkRestoreUrl: "/api/admin/reviews/restore",
        bulkForceUrl: "/api/admin/reviews/force-delete",
        isBulk: true,
    },
    {
        id: "feedback",
        label: "Feedback",
        fetchUrl: "/api/admin/feedback?trash=true",
        bulkRestoreUrl: "/api/admin/feedback/restore",
        bulkForceUrl: "/api/admin/feedback/force-delete",
        isBulk: true,
    },
];

const TrashAdmin = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const isArabic = i18n.language.startsWith('ar');

    const [activeTab, setActiveTab] = useState("products");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const tab = TABS.find(t => t.id === activeTab);

    useEffect(() => {
        fetchTrashItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

    const fetchTrashItems = async () => {
        setLoading(true);
        setSelectedIds([]);
        try {
            const res = await axios.get(`${API_URL}${tab.fetchUrl}`, { headers: authHeaders() });
            setItems(res.data.data || res.data || []);
        } catch (err) {
            console.error("Failed to fetch trash items:", err);
            alert(t("admin.trash_page.error_load", "Failed to load trash items"));
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (id) => {
        if (!window.confirm(t("admin.trash_page.confirm_restore", "Restore this item?"))) return;
        try {
            if (tab.isBulk) {
                await axios.post(`${API_URL}${tab.bulkRestoreUrl}`, { ids: [id] }, { headers: authHeaders() });
            } else {
                await axios[tab.restoreMethod](`${API_URL}${tab.restoreUrl(id)}`, {}, { headers: authHeaders() });
            }
            alert(t("admin.trash_page.restored", "Item restored successfully"));
            fetchTrashItems();
        } catch (err) {
            console.error("Restore failed:", err);
            alert(t("admin.trash_page.error_restore", "Failed to restore item"));
        }
    };

    const handleForceDelete = async (id) => {
        if (!window.confirm(t("admin.trash_page.confirm_force", "⚠️ PERMANENTLY DELETE this item? This cannot be undone!"))) return;
        try {
            if (tab.isBulk) {
                await axios.post(`${API_URL}${tab.bulkForceUrl}`, { ids: [id] }, { headers: authHeaders() });
            } else {
                await axios[tab.forceMethod](`${API_URL}${tab.forceUrl(id)}`, { headers: authHeaders() });
            }
            alert(t("admin.trash_page.deleted", "Item permanently deleted"));
            fetchTrashItems();
        } catch (err) {
            console.error("Force delete failed:", err);
            alert(t("admin.trash_page.error_force", "Failed to permanently delete item"));
        }
    };

    const handleBulkRestore = async () => {
        if (selectedIds.length === 0) return alert(t("admin.trash_page.no_selection", "No items selected"));
        if (!window.confirm(t("admin.trash_page.confirm_restore_bulk", { count: selectedIds.length, defaultValue: `Restore ${selectedIds.length} item(s)?` }))) return;
        try {
            if (tab.isBulk) {
                await axios.post(`${API_URL}${tab.bulkRestoreUrl}`, { ids: selectedIds }, { headers: authHeaders() });
            } else {
                for (const id of selectedIds) {
                    await axios[tab.restoreMethod](`${API_URL}${tab.restoreUrl(id)}`, {}, { headers: authHeaders() });
                }
            }
            alert(t("admin.trash_page.restored_bulk", "Items restored successfully"));
            fetchTrashItems();
        } catch (err) {
            console.error("Bulk restore failed:", err);
            alert(t("admin.trash_page.error_restore", "Failed to restore items"));
        }
    };

    const handleBulkForceDelete = async () => {
        if (selectedIds.length === 0) return alert(t("admin.trash_page.no_selection", "No items selected"));
        if (!window.confirm(t("admin.trash_page.confirm_force_bulk", { count: selectedIds.length, defaultValue: `⚠️ PERMANENTLY DELETE ${selectedIds.length} item(s)? This cannot be undone!` }))) return;
        try {
            if (tab.isBulk) {
                await axios.post(`${API_URL}${tab.bulkForceUrl}`, { ids: selectedIds }, { headers: authHeaders() });
            } else {
                for (const id of selectedIds) {
                    await axios[tab.forceMethod](`${API_URL}${tab.forceUrl(id)}`, { headers: authHeaders() });
                }
            }
            alert(t("admin.trash_page.deleted_bulk", "Items permanently deleted"));
            fetchTrashItems();
        } catch (err) {
            console.error("Bulk force delete failed:", err);
            alert(t("admin.trash_page.error_force", "Failed to permanently delete items"));
        }
    };

    const toggleSelection = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        setSelectedIds(selectedIds.length === items.length ? [] : items.map(item => item.id));
    };

    const renderItemName = (item) => {
        switch (activeTab) {
            case "products": return item.name || "Unnamed Product";
            case "categories": return item.name || "Unnamed Category";
            case "hero": return item.title || "Unnamed Slide";
            case "about": return item.title || "Unnamed Entry";
            case "contact_info": return item.email || "Contact Info Entry";
            case "contact_messages": return `${item.name} — ${item.subject || "No subject"}`;
            case "reviews": return `${item.name} — ${item.product_name || "Product"}`;
            case "feedback": return `${item.name || "Anonymous"} — ${(item.message || "").substring(0, 50)}`;
            default: return "Item";
        }
    };

    return (
        <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex flex-wrap justify-between items-center mb-8">
                <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">🗑️ {t("admin.trash_page.title", "Trash")}</h1>
                    <p className="text-gray-500 font-medium mt-1">{t("admin.trash_page.subtitle", "Restore or permanently delete items")}</p>
                </div>
                <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                    <span className={`inline-block transition-transform ${isArabic ? "rotate-180" : ""}`}>←</span>
                    {t('admin.common.back', 'Back')}
                </button>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
                {TABS.map(tabItem => (
                    <button
                        key={tabItem.id}
                        onClick={() => setActiveTab(tabItem.id)}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${activeTab === tabItem.id
                            ? "bg-red-500 text-white shadow-md"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                    >
                        {t(`admin.trash_page.tabs.${tabItem.id}`, tabItem.label)}
                    </button>
                ))}
            </div>

            {/* Bulk Actions Bar */}
            {items.length > 0 && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-2xl border border-gray-100">
                    <input
                        type="checkbox"
                        checked={selectedIds.length === items.length && items.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm font-medium text-gray-500">{t("admin.trash_page.selected", { count: selectedIds.length, defaultValue: `${selectedIds.length} selected` })}</span>
                    {selectedIds.length > 0 && (
                        <>
                            <button
                                onClick={handleBulkRestore}
                                className="px-4 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-xl transition-all"
                            >
                                {t("admin.trash_page.restore_selected", "Restore Selected")}
                            </button>
                            <button
                                onClick={handleBulkForceDelete}
                                className="px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-xl transition-all"
                            >
                                {t("admin.trash_page.delete_permanently", "Delete Permanently")}
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Content */}
            {loading ? (
                <div className="p-12 text-center text-gray-400 font-medium animate-pulse">{t("admin.trash_page.loading", "Loading...")}</div>
            ) : items.length === 0 ? (
                <div className="p-16 text-center">
                    <div className="text-6xl mb-4">🗑️</div>
                    <p className="text-gray-400 font-medium">{t("admin.trash_page.empty", "No items in trash for this section")}</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-100">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="p-3 text-left w-10">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.length === items.length}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 cursor-pointer"
                                    />
                                </th>
                                <th className="p-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">{t("admin.trash_page.col_id", "ID")}</th>
                                <th className="p-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">{t("admin.trash_page.col_name", "Name / Title")}</th>
                                <th className="p-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">{t("admin.trash_page.col_deleted_at", "Deleted At")}</th>
                                <th className="p-3 text-left font-bold text-gray-500 text-xs uppercase tracking-wider">{t("admin.trash_page.col_actions", "Actions")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {items.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={() => toggleSelection(item.id)}
                                            className="w-4 h-4 cursor-pointer"
                                        />
                                    </td>
                                    <td className="p-3 font-mono text-gray-400 text-xs">#{item.id}</td>
                                    <td className="p-3 font-medium text-gray-800">{renderItemName(item)}</td>
                                    <td className="p-3 text-gray-400 text-xs">{new Date(item.deleted_at).toLocaleString()}</td>
                                    <td className="p-3">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleRestore(item.id)}
                                                className="px-3 py-1.5 bg-green-50 text-green-600 hover:bg-green-500 hover:text-white text-xs font-bold rounded-xl transition-all"
                                            >
                                                {t("admin.trash_page.restore_btn", "Restore")}
                                            </button>
                                            <button
                                                onClick={() => handleForceDelete(item.id)}
                                                className="px-3 py-1.5 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white text-xs font-bold rounded-xl transition-all"
                                            >
                                                {t("admin.trash_page.force_btn", "Delete Forever")}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TrashAdmin;
